import { memo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import {
  Edit,
  Trash2,
  Ban,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { formatCurrency, formatDateTime } from "@/shared/lib/formatters";
import { parseSefazMessage } from "@/shared/lib/sefaz-message";
import { InvoiceDownloadActions } from "./InvoiceDownloadActions";
import { InvoiceStatusBadge } from "./InvoiceStatusBadge";
import type {
  InvoiceCategory,
  InvoiceResponseDTO,
  InvoicePaymentMethod,
  InvoiceType,
} from "../types/invoice.types";

const TYPE_LABEL: Record<InvoiceType, string> = {
  PAYMENT: "Pagamento",
  CANCELLATION: "Cancelamento",
};

const PAYMENT_METHOD_LABEL: Record<InvoicePaymentMethod, string> = {
  PIX: "PIX",
  CREDIT_CARD: "Crédito",
  DEBIT_CARD: "Débito",
  CASH: "Dinheiro",
};

interface InvoicesTableProps {
  invoices: InvoiceResponseDTO[];
  category: InvoiceCategory;
  loading: boolean;
  currentPage: number;
  totalPages: number;
  onEdit: (invoice: InvoiceResponseDTO) => void;
  onCancel: (invoice: InvoiceResponseDTO) => void;
  onDelete: (invoice: InvoiceResponseDTO) => void;
  onResend: (invoice: InvoiceResponseDTO) => void;
  onPageChange: (page: number) => void;
}

export const InvoicesTable = memo(function InvoicesTable({
  invoices,
  category,
  loading,
  currentPage,
  totalPages,
  onEdit,
  onCancel,
  onDelete,
  onResend,
  onPageChange,
}: InvoicesTableProps) {
  const referenceColumnLabel =
    category === "RENTAL"
      ? "Contrato / Cliente"
      : category === "GRADUATION"
        ? "Formatura"
        : "Origem";

  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>{referenceColumnLabel}</TableHead>
              <TableHead>ID Externo</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Pagamento</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead>Emissão</TableHead>
              <TableHead>Arquivos</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice) => {
              const referenceLabel =
                category === "RENTAL"
                  ? (invoice.contractNumber ??
                    (invoice.rentalId != null
                      ? `Locação #${invoice.rentalId}`
                      : "—"))
                  : category === "GRADUATION"
                    ? invoice.graduationId != null
                      ? `#${invoice.graduationId}`
                      : "—"
                    : "Avulsa";
              const canCancel = invoice.status === "ISSUED";
              const canResend = invoice.status === "ERROR";
              const readableError = parseSefazMessage(invoice.errorDescription);

              return (
                <TableRow key={invoice.id}>
                  <TableCell className="font-medium">{invoice.id}</TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span>{referenceLabel}</span>
                      {invoice.clientName && (
                        <span className="text-xs text-muted-foreground">
                          {invoice.clientName}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {invoice.externalId ?? "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <InvoiceStatusBadge status={invoice.status} />
                      {invoice.status === "ERROR" && (
                        <span
                          className="text-xs text-destructive max-w-xs"
                          title={
                            invoice.errorDescription ??
                            readableError?.message ??
                            undefined
                          }
                        >
                          {readableError?.message ??
                            "A SEFAZ recusou a nota. Confira os dados e retente a emissão."}
                          {readableError?.code
                            ? ` (código ${readableError.code})`
                            : ""}
                        </span>
                      )}
                      {invoice.status === "ERROR" &&
                        invoice.attemptCount !== null && (
                          <span className="text-xs text-muted-foreground">
                            Tentativas: {invoice.attemptCount}
                          </span>
                        )}
                    </div>
                  </TableCell>
                  <TableCell>{TYPE_LABEL[invoice.type]}</TableCell>
                  <TableCell>
                    {PAYMENT_METHOD_LABEL[invoice.paymentMethod]}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(invoice.value)}
                  </TableCell>
                  <TableCell>{formatDateTime(invoice.issuedAt)}</TableCell>
                  <TableCell>
                    <InvoiceDownloadActions invoice={invoice} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Can permission={ACTIONS.invoices.update}>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => onEdit(invoice)}
                          title="Editar"
                          aria-label="Editar nota fiscal"
                          disabled={loading}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </Can>
                      <Can permission={ACTIONS.invoices.create}>
                        <span
                          className="inline-flex"
                          title={
                            canResend
                              ? "Retentar emissão"
                              : "Só notas com erro podem ser reenviadas"
                          }
                        >
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => onResend(invoice)}
                            aria-label={
                              canResend
                                ? "Retentar emissão da nota fiscal"
                                : "Retentar emissão indisponível: só notas com erro podem ser reenviadas"
                            }
                            disabled={loading || !canResend}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        </span>
                      </Can>
                      <Can permission={ACTIONS.invoices.cancel}>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => onCancel(invoice)}
                          title={
                            canCancel
                              ? "Cancelar nota"
                              : "Apenas notas emitidas podem ser canceladas"
                          }
                          aria-label="Cancelar nota fiscal"
                          disabled={loading || !canCancel}
                        >
                          <Ban className="w-4 h-4 text-amber-600" />
                        </Button>
                      </Can>
                      <Can permission={ACTIONS.invoices.delete}>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => onDelete(invoice)}
                          title="Excluir"
                          aria-label="Excluir nota fiscal"
                          disabled={loading}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </Can>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-col md:flex-row items-center justify-between mt-4 pt-4 border-t gap-4">
          <div className="text-sm text-muted-foreground">
            Página {currentPage + 1} de {totalPages}
          </div>
          <div className="flex gap-2 items-center flex-wrap justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0 || loading}
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>

            <div className="flex gap-1 flex-wrap justify-center">
              {Array.from({ length: totalPages }, (_, i) => {
                const showPage =
                  i === 0 ||
                  i === totalPages - 1 ||
                  (i >= currentPage - 1 && i <= currentPage + 1);

                const showEllipsis =
                  (i === 1 && currentPage > 2) ||
                  (i === totalPages - 2 && currentPage < totalPages - 3);

                if (showEllipsis) {
                  return (
                    <span
                      key={i}
                      className="px-2 py-1 text-sm text-muted-foreground"
                    >
                      ...
                    </span>
                  );
                }

                if (!showPage) return null;

                return (
                  <Button
                    key={i}
                    variant={currentPage === i ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(i)}
                    disabled={loading}
                    className="min-w-10"
                  >
                    {i + 1}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1 || loading}
            >
              Próxima
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
});
